import { TextField } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'
import { getErrorMessage } from '@/scopes/common/components/FormElements/getErrorMessage'
import { useStatus } from '@/scopes/common/components/FormElements/useStatus'
import { DECIMAL_INPUT_PATTERN, LETTER_PATTERN } from '@/scopes/common/components/FormElements/utils/decimalPatternUtils'

export interface TextInputProps {
  name: string
  label?: string
  pattern?: string
  patternErrorMessage?: string
  required?: boolean
  requiredErrorMessage?: string
  formFieldClass?: string
  helpText?: string
  fieldLengthValidation?: number
  disabled?: boolean
  autoComplete?: string
}

export function TextInput({
  name,
  label,
  pattern,
  patternErrorMessage,
  required,
  requiredErrorMessage,
  formFieldClass,
  helpText,
  fieldLengthValidation = 1,
  disabled,
  autoComplete,
}: TextInputProps) {
  const intl = useIntl()
  const disallowLetters = pattern === DECIMAL_INPUT_PATTERN
  const {
    register,
    formState,
  } = useFormContext()
  const validations = React.useMemo(
    () => ({
      ...(pattern ? { matchPattern: (value: any) => validatePattern(value, pattern) } : {}),
    }),
    [pattern],
  )

  const { onChange: registerOnChange, ...registration } = register(name, {
    required,
    disabled,
    validate: validations,
    setValueAs: (value: string) => (typeof value === 'string' ? value.trim() : value),
  })

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (disallowLetters)
      event.target.value = event.target.value.replace(LETTER_PATTERN, '')

    registerOnChange(event)
  }, [disallowLetters, registerOnChange])

  const { errors } = formState
  const shouldMarkValidStatus = React.useCallback(
    (value: string) => value?.trim().length >= fieldLengthValidation,
    [fieldLengthValidation],
  )
  const status = useStatus({
    name,
    required,
    validations,
    shouldMarkValidStatus,
  })
  const errorMessage = getErrorMessage(
    name,
    errors,
    { patternErrorMessage, requiredErrorMessage },
  )
  return (
    <TextField
      label={label}
      feedbackText={errorMessage}
      status={status}
      disabled={disabled}
      inputProps={{
        ...registration,
        onChange: handleChange,
        autoComplete,
      }}
      formFieldClass={formFieldClass}
      optionalLabel={!required ? intl.formatMessage({ id: 'common_form_field_optional_label' }) : undefined}
      helpText={helpText}
      name={name}
    />
  )
}

export function validatePattern(value: any, pattern: string) {
  if (typeof value !== 'string')
    return true
  const normalized = value.trim().replace(/\s+/g, ' ')

  if (!normalized) {
    return true
  }

  return new RegExp(pattern).test(normalized)
}
